import { gql, useQuery } from "@apollo/client";
import { makeStyles, Typography } from "@material-ui/core";
import { strings } from "@resource/common";
import ConfirmEdits from "components/legacy/BulkEdit/EditStageTemplateInstallations/ConfirmEdits";
import EditStages from "components/legacy/BulkEdit/EditStageTemplateInstallations/EditStages";
import SelectStages from "components/legacy/BulkEdit/EditStageTemplateInstallations/SelectStages";
import {
  ActionEnum,
  BulkStageEditMode,
  StageEditsDispatchType,
  StageEditsStateType,
} from "components/legacy/BulkEdit/EditStageTemplateInstallations/types";
import ProgressStepper from "components/legacy/BulkEdit/ProgressStepper";
import Dialog from "components/legacy/Dialog/Dialog";
import DialogTitle from "components/legacy/Dialog/DialogTitle/DialogTitle";
import Loading from "components/legacy/Loading/Loading";
import {
  JobRoleGuideTemplatesForBulkEdit,
  JobRoleGuideTemplatesForBulkEditVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import { Reducer, useMemo, useReducer } from "react";
import { ResourceCustomTheme } from "styles/config";

enum ProgressStepEnum {
  SetStages = 0,
  SetEdits = 1,
  ConfirmEdits = 2,
}

const useStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {},
  titleContainer: {
    textAlign: "center",
  },
  emphasize: {
    color: theme.colors.RoyalPurple(),
  },
  contextLabel: {
    marginBottom: theme.spacing(2),
  },
}));

const JOB_ROLE_GUIDE_TEMPLATES_FOR_BULK_EDIT = gql`
  query JobRoleGuideTemplatesForBulkEdit(
    $where: OrganizationJobRoleGuideTemplateWhereInput
  ) {
    currentOrganization {
      id
      jobRoleGuideTemplates(where: $where) {
        id
        name
        stageTemplateInstallations {
          id
          activatedAt
          atsStageId
        }
        jobRole {
          id
          atsJob {
            id
            name
            atsJobStages {
              id
              name
              position
              originalATSId
              jobId
            }
          }
        }
        updatedAt
      }
    }
  }
`;

type BulkEditStageTemplateInstallationsModalProps = {
  jobRoleGuideTemplateIds: string[];
  open: boolean;
  onCancel: () => void;
  onClose: () => void;
  onExited: () => void;
  mode?: BulkStageEditMode;
};

function BulkEditStageTemplateInstallationsModal({
  jobRoleGuideTemplateIds,
  mode = BulkStageEditMode.Edit,
  open,
  onCancel,
  onExited,
  onClose,
}: BulkEditStageTemplateInstallationsModalProps) {
  const classes = useStyles();
  const reducer = (
    state: StageEditsStateType,
    action: StageEditsDispatchType
  ): StageEditsStateType => {
    switch (action.type) {
      case ActionEnum.SetStages:
        return {
          ...state,
          stageIds: action.stageIds ?? [],
        };
      case ActionEnum.SetEdits:
        return {
          ...state,
          ...(action.edits ?? {}),
        };
      case ActionEnum.Next:
        return {
          ...state,
          activeStepIndex: state.activeStepIndex + 1,
        };
      case ActionEnum.Back:
        return {
          ...state,
          activeStepIndex: state.activeStepIndex - 1,
        };
      case ActionEnum.Cancel:
        onCancel();
        return state;
      case ActionEnum.Finish:
        onClose();
        return state;
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer<
    Reducer<StageEditsStateType, StageEditsDispatchType>
  >(reducer, {
    stageIds: [],
    activeStepIndex: 0,
  });

  const { data, loading } = useQuery<
    JobRoleGuideTemplatesForBulkEdit,
    JobRoleGuideTemplatesForBulkEditVariables
  >(JOB_ROLE_GUIDE_TEMPLATES_FOR_BULK_EDIT, {
    variables: {
      where: {
        id: {
          _in: jobRoleGuideTemplateIds,
        },
        isArchived: false,
      },
    },
  });

  const jobRoleGuideTemplates = useMemo(
    () => data?.currentOrganization?.jobRoleGuideTemplates ?? [],
    [data]
  );

  let steps: ProgressStepEnum[] = [];
  switch (mode) {
    case BulkStageEditMode.Add:
      steps = [
        ProgressStepEnum.SetStages,
        ProgressStepEnum.SetEdits,
        ProgressStepEnum.ConfirmEdits,
      ];
      break;
    case BulkStageEditMode.Edit:
      steps = [
        ProgressStepEnum.SetStages,
        ProgressStepEnum.SetEdits,
        ProgressStepEnum.ConfirmEdits,
      ];
      break;
    case BulkStageEditMode.Remove:
      steps = [ProgressStepEnum.SetStages, ProgressStepEnum.ConfirmEdits];
      break;

    default:
      break;
  }

  const stepLabelForIndex = (idx: number) => {
    switch (mode) {
      case BulkStageEditMode.Add:
        return ["Select Stages", "Configure Stages", "Confirm"][idx];
      case BulkStageEditMode.Edit:
        return ["Select Stages", "Edit Stages", "Confirm"][idx];
      case BulkStageEditMode.Remove:
        return ["Select Stages", "Confirm"][idx];
      default:
        return "";
    }
  };

  const numSelectedJobRoleGuideTemplates = useMemo(
    () =>
      _.filter(
        jobRoleGuideTemplates,
        (jobRoleGuideTemplate) =>
          !!_.find(
            jobRoleGuideTemplate.jobRole?.atsJob?.atsJobStages,
            ({ originalATSId }) => _.includes(state.stageIds, originalATSId)
          )
      ).length,
    [jobRoleGuideTemplates, state.stageIds]
  );

  let verb = "";
  switch (mode) {
    case BulkStageEditMode.Add:
      verb = "adding";
      break;
    case BulkStageEditMode.Edit:
      verb = "editing";
      break;
    case BulkStageEditMode.Remove:
      verb = "removing";
      break;
    default:
  }

  let selectionCopy = "";
  if (steps[state.activeStepIndex] === ProgressStepEnum.ConfirmEdits) {
    selectionCopy = `You are ${
      mode === BulkStageEditMode.Edit ? "applying the following edits to" : verb
    }`;
  } else {
    selectionCopy = `You are ${verb}`;
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      onExited={onExited}
      maxWidth="md"
      scroll="paper"
      title={`${mode} Stages`}
      fullWidth
      className={classes.root}
    >
      {loading ? (
        <Loading />
      ) : (
        <>
          <DialogTitle onClose={onCancel}>
            <div className={classes.titleContainer}>
              <ProgressStepper
                steps={_.map(steps, (_s, idx) => stepLabelForIndex(idx))}
                activeStep={state.activeStepIndex}
              />

              <Typography className={classes.contextLabel} variant="h4">
                {state.stageIds.length ? (
                  <>
                    {selectionCopy}{" "}
                    <span className={classes.emphasize}>
                      {strings.pluralize("stage", state.stageIds.length)}
                    </span>{" "}
                    within{" "}
                    <span className={classes.emphasize}>
                      {strings.pluralize(
                        "journey",
                        numSelectedJobRoleGuideTemplates
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    You selected{" "}
                    <span className={classes.emphasize}>
                      {strings.pluralize(
                        "journey",
                        jobRoleGuideTemplates.length
                      )}
                    </span>
                  </>
                )}
              </Typography>
            </div>
          </DialogTitle>
          {(() => {
            let ToRender;
            switch (steps[state.activeStepIndex]) {
              case ProgressStepEnum.SetStages:
                ToRender = (
                  <SelectStages
                    mode={mode}
                    state={state}
                    dispatch={dispatch}
                    jobRoleGuideTemplates={jobRoleGuideTemplates}
                  />
                );
                break;
              case ProgressStepEnum.SetEdits:
                ToRender = (
                  <EditStages mode={mode} state={state} dispatch={dispatch} />
                );
                break;
              case ProgressStepEnum.ConfirmEdits:
                ToRender = (
                  <ConfirmEdits
                    mode={mode}
                    state={state}
                    dispatch={dispatch}
                    jobRoleGuideTemplates={jobRoleGuideTemplates}
                  />
                );
                break;

              default:
                break;
            }
            return <>{ToRender}</>;
          })()}
        </>
      )}
    </Dialog>
  );
}

export default BulkEditStageTemplateInstallationsModal;
